/**
 * Touch controls for the RTS canvas, built on Pointer Events.
 *
 * Only pointers with `pointerType === "touch"` are handled; mouse and pen
 * keep their existing mouse-event handlers in selection.ts / camera.ts /
 * main.ts. A `touchstart` preventDefault suppresses the compatibility mouse
 * events browsers would otherwise fire after touchend, so a tap never
 * double-triggers the mouse click path.
 *
 * Gestures:
 * - tap (quick, < 12px movement): onTap — smart-tap routing lives in main.ts
 * - one-finger drag: onPan(dxPx, dyPx) — camera pan
 * - one-finger drag in select mode: onSelectDrag(rect)/onSelect(rect) —
 *   rubber-band box selection instead of panning
 * - two-finger pinch: onPinch(zoomFactor, panDxPx, panDyPx) — camera zoom + pan
 * - two-finger twist: onRotate(deltaYaw) — camera rotate
 */
export interface SelectRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface TouchCallbacks {
  onTap: (clientX: number, clientY: number) => void;
  onPan: (dxPx: number, dyPx: number) => void;
  onPinch: (zoomFactor: number, panDxPx: number, panDyPx: number) => void;
  /** Two-finger twist. Called with the yaw delta in radians (optional). */
  onRotate?: (deltaYaw: number) => void;
  /** Select mode: live rubber-band rect in CSS px; null clears it. */
  onSelectDrag?: (rect: SelectRect | null) => void;
  /** Select mode: finger lifted after a real drag — run the box select. */
  onSelect?: (rect: SelectRect) => void;
}

interface Tracked {
  x: number;
  y: number;
  startX: number;
  startY: number;
  startT: number;
  /** Last position a pan delta was measured from. */
  lastX: number;
  lastY: number;
  moved: boolean;
}

/** Max finger travel (CSS px) that still counts as a tap. */
const TAP_SLOP = 12;
/** Max tap duration (ms). */
const TAP_TIME = 600;

export class TouchControls {
  private tracked = new Map<number, Tracked>();
  /** Baseline pinch distance / midpoint, valid while two pointers are down. */
  private pinchDist = 0;
  private pinchMidX = 0;
  private pinchMidY = 0;
  /** Baseline two-finger angle, valid while two pointers are down. */
  private pinchAngle = 0;
  /**
   * Select mode: one-finger drags draw a selection box instead of panning.
   * Taps still route through onTap; pinch/twist are unaffected.
   */
  selectMode = false;

  constructor(
    private readonly dom: HTMLElement,
    private readonly cb: TouchCallbacks,
  ) {
    // Suppress compatibility mouse events + browser gestures on the canvas.
    // (Page-level pinch-zoom/scroll is already disabled via touch-action CSS.)
    dom.addEventListener("touchstart", (e) => e.preventDefault(), {
      passive: false,
    });
    dom.addEventListener("pointerdown", (e) => this.down(e));
    window.addEventListener("pointermove", (e) => this.move(e));
    window.addEventListener("pointerup", (e) => this.up(e));
    window.addEventListener("pointercancel", (e) => this.up(e));
  }

  private down(e: PointerEvent): void {
    if (e.pointerType !== "touch") return;
    if (e.target !== this.dom) return;
    this.tracked.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
      startX: e.clientX,
      startY: e.clientY,
      startT: performance.now(),
      lastX: e.clientX,
      lastY: e.clientY,
      moved: false,
    });
    if (this.tracked.size === 2) this.resetPinchBaseline();
  }

  private move(e: PointerEvent): void {
    const t = this.tracked.get(e.pointerId);
    if (!t) return;
    t.x = e.clientX;
    t.y = e.clientY;

    if (this.tracked.size === 2) {
      const [a, b] = [...this.tracked.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2;
      const angle = Math.atan2(b.y - a.y, b.x - a.x);
      if (this.pinchDist > 0 && dist > 0) {
        // Fingers apart -> zoom in -> factor < 1 (matches camera.zoomBy).
        this.cb.onPinch(this.pinchDist / dist, midX - this.pinchMidX, midY - this.pinchMidY);
        // Twist -> rotate. Wrapped to [-PI, PI] so the 359°/-1° seam
        // doesn't fling the camera.
        let dAngle = angle - this.pinchAngle;
        while (dAngle > Math.PI) dAngle -= 2 * Math.PI;
        while (dAngle < -Math.PI) dAngle += 2 * Math.PI;
        if (dAngle !== 0) this.cb.onRotate?.(dAngle);
      }
      this.pinchDist = dist;
      this.pinchMidX = midX;
      this.pinchMidY = midY;
      this.pinchAngle = angle;
      a.moved = b.moved = true;
      a.lastX = a.x; a.lastY = a.y;
      b.lastX = b.x; b.lastY = b.y;
      return;
    }

    // Single-finger drag -> pan once past the tap slop, or a selection
    // box in select mode.
    const dx = t.x - t.lastX;
    const dy = t.y - t.lastY;
    if (
      !t.moved &&
      Math.hypot(t.x - t.startX, t.y - t.startY) > TAP_SLOP
    ) {
      t.moved = true;
    }
    if (t.moved && (dx !== 0 || dy !== 0)) {
      if (this.selectMode) {
        this.cb.onSelectDrag?.({
          x0: t.startX,
          y0: t.startY,
          x1: t.x,
          y1: t.y,
        });
      } else {
        this.cb.onPan(dx, dy);
      }
      t.lastX = t.x;
      t.lastY = t.y;
    }
  }

  private up(e: PointerEvent): void {
    const t = this.tracked.get(e.pointerId);
    if (!t) return;
    this.tracked.delete(e.pointerId);
    if (
      !t.moved &&
      performance.now() - t.startT < TAP_TIME &&
      this.tracked.size === 0
    ) {
      this.cb.onTap(t.startX, t.startY);
    }
    // Select mode: a real drag becomes a box select. The rect must exceed
    // the tap slop so pinch leftovers can't trigger a bogus selection.
    if (
      this.selectMode &&
      t.moved &&
      this.tracked.size === 0 &&
      Math.hypot(t.x - t.startX, t.y - t.startY) > TAP_SLOP
    ) {
      this.cb.onSelect?.({ x0: t.startX, y0: t.startY, x1: t.x, y1: t.y });
      this.cb.onSelectDrag?.(null);
    }
    // Dropping from two fingers to one: re-anchor the survivor so the
    // remaining finger doesn't jump-pan or fire a stale tap. The drag
    // origin is reset too so a post-pinch drag measures from here.
    if (this.tracked.size === 1) {
      const survivor = [...this.tracked.values()][0];
      survivor.moved = true;
      survivor.startX = survivor.x;
      survivor.startY = survivor.y;
      survivor.lastX = survivor.x;
      survivor.lastY = survivor.y;
    }
    if (this.tracked.size < 2) this.pinchDist = 0;
  }

  private resetPinchBaseline(): void {
    const [a, b] = [...this.tracked.values()];
    this.pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
    this.pinchMidX = (a.x + b.x) / 2;
    this.pinchMidY = (a.y + b.y) / 2;
    this.pinchAngle = Math.atan2(b.y - a.y, b.x - a.x);
  }
}
